import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDownIcon } from '../../../assets/icons/icons';
import { Popover } from './Popover';
import { usePopover } from './usePopover';
import styles from './DatePicker.module.scss';

export type TimeValue = string;

export interface TimePickerProps {
    value: TimeValue | '';
    onChange: (value: TimeValue | '') => void;
    placeholder?: string;
    disabled?: boolean;
    /** Шаг списка минут. По умолчанию поминутно; с клавиатуры можно ввести любое значение. */
    minuteStep?: 1 | 5 | 10 | 15 | 30;
    clearable?: boolean;
    'aria-label'?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

const parse = (value: string): [number | null, number | null] => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value);
    if (!match) return [null, null];

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return [null, null];

    return [hours, minutes];
};

/** Цифры из поля → «ЧЧ:ММ». «9» → 09:00, «930» → 09:30, «1945» → 19:45. */
const complete = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    if (!digits) return '';

    if (digits.length <= 2) return `${digits.padStart(2, '0')}:00`;
    if (digits.length === 3) return `0${digits[0]}:${digits.slice(1)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const ClockIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
    </svg>
);

interface ColumnProps {
    label: string;
    items: number[];
    selected: number | null;
    onPick: (value: number) => void;
}

const Column = ({ label, items, selected, onPick }: ColumnProps) => {
    const listRef = useRef<HTMLDivElement>(null);

    /* При открытии подкручиваем список к выбранному значению. */
    useEffect(() => {
        const list = listRef.current;
        if (!list || selected === null) return;

        const active = list.querySelector<HTMLElement>('[aria-pressed="true"]');
        if (!active) return;

        list.scrollTop = active.offsetTop - list.clientHeight / 2 + active.offsetHeight / 2;
    }, [selected]);

    return (
        <div className={styles.timeColumn}>
            <span className={styles.timeColumnLabel}>{label}</span>
            <div className={styles.timeList} ref={listRef}>
                {items.map((item) => (
                    <button
                        type="button"
                        key={item}
                        className={`${styles.timeItem} ${item === selected ? styles.timeItemActive : ''}`}
                        onClick={() => onPick(item)}
                        aria-pressed={item === selected}
                    >
                        {pad(item)}
                    </button>
                ))}
            </div>
        </div>
    );
};

export const TimePicker = ({
    value,
    onChange,
    placeholder = 'чч:мм',
    disabled = false,
    minuteStep = 1,
    clearable = true,
    'aria-label': ariaLabel,
}: TimePickerProps) => {
    const { isOpen, setIsOpen, rootRef, popoverRef, position } = usePopover<HTMLDivElement>();
    const popoverId = useId();
    const inputRef = useRef<HTMLInputElement>(null);

    const [draft, setDraft] = useState(value);

    useEffect(() => setDraft(value), [value]);

    const [hours, minutes] = parse(value);

    const hourItems = Array.from({ length: 24 }, (_, i) => i);

    const minuteItems = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep);
    if (minutes !== null && !minuteItems.includes(minutes)) {
        minuteItems.push(minutes);
        minuteItems.sort((a, b) => a - b);
    }

    const handleInput = (raw: string): void => {
        const digits = raw.replace(/\D/g, '').slice(0, 4);
        const next = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;

        setDraft(next);

        const [h, m] = parse(next);
        if (h !== null && m !== null) onChange(next);
        if (!next) onChange('');
    };

    const commit = (): void => {
        if (!draft.trim()) {
            setDraft('');
            onChange('');
            return;
        }

        const next = complete(draft);
        const [h, m] = parse(next);

        if (h === null || m === null) {
            setDraft(value);
            return;
        }

        setDraft(next);
        onChange(next);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commit();
            setIsOpen(false);
            return;
        }

        if (event.key === 'Escape') {
            setDraft(value);
            setIsOpen(false);
            return;
        }

        if (event.key === 'ArrowDown' && !isOpen) setIsOpen(true);
    };

    const pickHours = (h: number) => onChange(`${pad(h)}:${pad(minutes ?? 0)}`);

    const pickMinutes = (m: number) => {
        onChange(`${pad(hours ?? 0)}:${pad(m)}`);
        /* Минуты выбираются последними — закрываем, как календарь после клика по дню. */
        setIsOpen(false);
    };

    return (
        <div className={styles.picker} ref={rootRef}>
            <div
                className={`${styles.trigger} ${styles.triggerInput} ${disabled ? styles.triggerDisabled : ''}`}
            >
                <span className={styles.triggerLeading}>
                    <ClockIcon className={styles.icon} />
                </span>

                <input
                    ref={inputRef}
                    type="text"
                    className={styles.timeInput}
                    value={draft}
                    placeholder={placeholder}
                    disabled={disabled}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={5}
                    aria-label={ariaLabel}
                    onChange={(event) => handleInput(event.target.value)}
                    onBlur={commit}
                    onKeyDown={handleKeyDown}
                    onFocus={(event) => event.target.select()}
                    style={{
                        background: 'none',
                        border: 'none',
                        borderRadius: 0,
                        boxShadow: 'none',
                        color: 'var(--color-muted)',
                        fontSize: 'var(--fs-dash-body-sm)',
                    }}
                />

                <button
                    type="button"
                    className={styles.timeCaret}
                    onClick={() => {
                        setIsOpen(!isOpen);
                        inputRef.current?.focus();
                    }}
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? popoverId : undefined}
                    aria-label="Открыть список времени"
                >
                    <ChevronDownIcon className={styles.icon} />
                </button>
            </div>

            {isOpen && (
                <Popover id={popoverId} label="Выбор времени" popoverRef={popoverRef} position={position}>
                    <div className={styles.timeColumns}>
                        <Column label="Часы" items={hourItems} selected={hours} onPick={pickHours} />
                        <Column label="Минуты" items={minuteItems} selected={minutes} onPick={pickMinutes} />
                    </div>

                    {clearable && (
                        <div className={styles.popoverFooter}>
                            <span className={styles.hint}>{value || placeholder}</span>
                            <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() => {
                                    onChange('');
                                    setDraft('');
                                    setIsOpen(false);
                                }}
                                disabled={!value}
                            >
                                Сбросить
                            </button>
                        </div>
                    )}
                </Popover>
            )}
        </div>
    );
};